import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Gift, Sparkles, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { usePurchase } from "./PurchaseButton";
import { scrollToId } from "./Navbar";
import { track } from "../utils/track";
import {
  getVisitorState,
  setVisitorState,
  isDiscountActive,
  discountRemainingMs,
  DISCOUNT_DURATION_MS,
  CLEAR_HEAD_DISMISS_DAYS,
} from "../utils/visitorState";
import {
  PRICE,
  DISCOUNT,
  DISCOUNT_ENABLED,
  DISCOUNT_PAYMENT_URL,
} from "../config/brand";

const LEAD_URL = "/delivery/subscribe.php";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useCountdown = (onExpire) => {
  const [left, setLeft] = useState(discountRemainingMs());
  const prev = useRef(discountRemainingMs());
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  useEffect(() => {
    const id = setInterval(() => {
      const remaining = discountRemainingMs();
      setLeft(remaining);
      if (prev.current > 0 && remaining <= 0) {
        onExpireRef.current?.();
      }
      prev.current = remaining;
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const total = Math.ceil(left / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const EmailForm = ({ source, buttonLabel, onSuccess, testIdPrefix, showName = false }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    track(`${source}_form_submit`);
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address.");
      track(`${source}_form_error`);
      return;
    }
    setError("");
    setBusy(true);
    try {
      const res = await fetch(LEAD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          source,
          ...(showName && name.trim() ? { name: name.trim() } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error("failed");
      track(`${source}_form_success`);
      onSuccess(email.trim());
    } catch {
      setError("Something went wrong on our side. Please try again in a moment.");
      track(`${source}_form_error`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="mt-6 space-y-3">
      {showName && (
        <>
          <label
            htmlFor={`${testIdPrefix}-name`}
            className="block text-xs font-bold uppercase tracking-[0.18em] text-plum"
          >
            First name <span className="font-semibold normal-case tracking-normal text-plumcharcoal/45">(optional)</span>
          </label>
          <input
            id={`${testIdPrefix}-name`}
            type="text"
            autoComplete="given-name"
            placeholder="Your first name"
            value={name}
            data-testid={`${testIdPrefix}-name-input`}
            onChange={(e) => setName(e.target.value)}
            className="-mt-1 w-full rounded-xl border border-mauve/50 bg-ivory px-4 py-3.5 text-base text-plumcharcoal transition-colors focus:border-plum focus:outline-none"
          />
        </>
      )}
      <label
        htmlFor={`${testIdPrefix}-email`}
        className="block text-xs font-bold uppercase tracking-[0.18em] text-plum"
      >
        Your email address
      </label>
      <input
        id={`${testIdPrefix}-email`}
        type="email"
        autoComplete="email"
        placeholder="Enter your email address"
        value={email}
        aria-invalid={!!error}
        aria-describedby={error ? `${testIdPrefix}-error` : undefined}
        data-testid={`${testIdPrefix}-email-input`}
        onFocus={() => {
          if (!started.current) {
            started.current = true;
            track(`${source}_form_start`);
          }
        }}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full rounded-xl border border-mauve/50 bg-ivory px-4 py-3.5 text-base text-plumcharcoal transition-colors focus:border-plum focus:outline-none"
      />
      {error && (
        <p id={`${testIdPrefix}-error`} role="alert" data-testid={`${testIdPrefix}-error`} className="text-sm font-semibold text-rose">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        data-testid={`${testIdPrefix}-submit`}
        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-rose px-6 py-4 text-sm font-bold uppercase tracking-[0.08em] text-warmwhite shadow-[0_10px_26px_rgba(201,130,150,0.4)] transition-[background-color,transform] duration-200 hover:bg-[#b96f84] active:scale-[0.98] disabled:opacity-60"
      >
        {busy ? "One moment…" : buttonLabel}
        {!busy && <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />}
      </button>
      <p className="text-center text-[11px] leading-relaxed text-plumcharcoal/50">
        By submitting, you agree to receive BloomParentify emails. Unsubscribe
        anytime.
      </p>
    </form>
  );
};

const PopupShell = ({ icon: Icon, eyebrow, children, testId }) => (
  <div data-testid={testId}>
    <div className="flex justify-center">
      <motion.span
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blushcream text-plum"
      >
        <Icon size={24} strokeWidth={1.5} />
      </motion.span>
    </div>
    <p className="mt-5 text-center text-[11px] font-bold uppercase tracking-[0.22em] text-rose">
      {eyebrow}
    </p>
    {children}
  </div>
);

export const OfferManager = () => {
  const location = useLocation();
  const [mode, setMode] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const openRef = useRef(false);
  const shownRef = useRef({ clearhead: false, discount: false });
  const reduce = useReducedMotion();

  const setOpenSafe = (v) => {
    openRef.current = v;
    setOpen(v);
  };

  const openOffer = (m, manual = false) => {
    if (openRef.current) return;
    if (!manual && document.querySelector('[role="dialog"]')) return;
    const s = getVisitorState();
    if (s.discountClaimed) return;
    if (m === "clearhead") {
      if (!manual) {
        if (s.clearHeadSubmitted) return;
        if (shownRef.current.clearhead) return;
        if (
          s.clearHeadDismissedAt &&
          Date.now() - s.clearHeadDismissedAt < CLEAR_HEAD_DISMISS_DAYS * 24 * 3600 * 1000
        )
          return;
        shownRef.current.clearhead = true;
      }
      track("clear_head_popup_open");
    } else {
      if (!DISCOUNT_ENABLED) return;
      if (!manual && shownRef.current.discount) return;
      shownRef.current.discount = true;
      track("discount_popup_open");
    }
    setSubmitted(manual && m === "clearhead" && !!s.clearHeadSubmitted);
    setMode(m);
    setOpenSafe(true);
  };

  const dismiss = () => {
    if (mode === "clearhead") {
      setVisitorState({ clearHeadDismissedAt: Date.now() });
      track("clear_head_popup_dismiss");
    } else if (mode === "discount") {
      track("discount_popup_dismiss");
    }
    setOpenSafe(false);
  };

  useEffect(() => {
    if (location.pathname !== "/") return;
    const s0 = getVisitorState();
    if (s0.discountClaimed) return;

    const desktop = window.matchMedia("(pointer: fine)").matches;
    const start = Date.now();
    let maxScroll = 0;
    let clicks = 0;
    let offerReached = false;

    const score = () =>
      (offerReached ? 2 : 0) + Math.min(clicks, 2) + (maxScroll >= 60 ? 1 : 0);
    const engaged = () => maxScroll >= 40 || Date.now() - start >= 25000;

    const maybeClearHead = () => {
      // Desktop: exit intent is the ONLY trigger. Mobile: ~75% scroll depth.
      if (desktop) return;
      if (maxScroll < 75) return;
      if (DISCOUNT_ENABLED) {
        if (score() >= 2) return;
        const offerEl = document.getElementById("offer");
        if (offerEl && offerEl.getBoundingClientRect().top < window.innerHeight * 0.75) return;
      }
      openOffer("clearhead");
    };
    const maybeDiscount = () => {
      if (score() >= 3) openOffer("discount");
    };

    const onScroll = () => {
      const h = document.documentElement;
      const pct = (h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight)) * 100;
      if (pct > maxScroll) maxScroll = pct;
      maybeClearHead();
      maybeDiscount();
    };
    const onIntent = () => {
      clicks += 1;
      maybeDiscount();
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          offerReached = true;
          maybeDiscount();
        }
      },
      { threshold: 0.25 }
    );
    const offerEl = document.getElementById("offer");
    if (offerEl) io.observe(offerEl);

    const onExit = (e) => {
      if (!desktop || e.clientY > 0 || e.relatedTarget) return;
      if (!engaged()) return;
      if (DISCOUNT_ENABLED && score() >= 3) openOffer("discount");
      else openOffer("clearhead");
    };
    const onManual = () => openOffer("clearhead", true);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("bp-purchase-intent", onIntent);
    document.addEventListener("mouseout", onExit);
    window.addEventListener("bp-open-clearhead", onManual);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("bp-purchase-intent", onIntent);
      document.removeEventListener("mouseout", onExit);
      window.removeEventListener("bp-open-clearhead", onManual);
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleClearHeadSuccess = () => {
    setVisitorState({ clearHeadSubmitted: true });
    setSubmitted(true);
  };

  const handleDiscountSuccess = () => {
    setVisitorState({
      discountClaimed: true,
      discountExpiresAt: Date.now() + DISCOUNT_DURATION_MS,
    });
    track("discount_timer_started");
    setSubmitted(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) dismiss();
        }}
      >
        <DialogContent
          aria-label={mode === "discount" ? "Special first-purchase offer" : "Free Clear Head Kit"}
          className="max-h-[92vh] max-w-md overflow-y-auto rounded-3xl border-mauve/40 bg-warmwhite p-7 sm:p-9"
          data-testid="offer-popup"
        >
          <AnimatePresence mode="wait">
            {mode === "clearhead" && !submitted && (
              <motion.div
                key="clearhead"
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <PopupShell icon={Gift} eyebrow="A free resource for the overwhelmed working mom" testId="clear-head-popup">
                  <DialogHeader>
                    <DialogTitle className="mt-3 text-center font-serif text-3xl font-medium leading-tight tracking-tight text-plum">
                      Clear your head. Make room for what matters.
                    </DialogTitle>
                    <DialogDescription className="mt-3 text-center text-base leading-relaxed text-plumcharcoal/70">
                      The Clear Head Kit is a simple collection of practical
                      tools to help you get the moving parts of everyday life
                      out of your head and onto paper.
                    </DialogDescription>
                  </DialogHeader>
                  <EmailForm
                    source="clear_head"
                    buttonLabel="Get the free Clear Head Kit"
                    onSuccess={handleClearHeadSuccess}
                    testIdPrefix="clear-head"
                    showName
                  />
                  <p className="mt-3 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-plumcharcoal/45">
                    Free resource • Delivered to your inbox
                  </p>
                </PopupShell>
              </motion.div>
            )}

            {mode === "clearhead" && submitted && (
              <motion.div
                key="clearhead-success"
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <PopupShell icon={Gift} eyebrow="Done" testId="clear-head-success">
                  <DialogHeader>
                    <DialogTitle className="mt-3 text-center font-serif text-3xl font-medium leading-tight tracking-tight text-plum">
                      Your Clear Head Kit is on its way.
                    </DialogTitle>
                    <DialogDescription className="mt-3 text-center text-base leading-relaxed text-plumcharcoal/70">
                      Check your inbox for your free resource.
                    </DialogDescription>
                  </DialogHeader>
                  <button
                    type="button"
                    data-testid="clear-head-explore"
                    onClick={() => {
                      setOpenSafe(false);
                      setTimeout(() => scrollToId("offer"), 250);
                    }}
                    className="group mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-plum px-6 py-4 text-sm font-bold uppercase tracking-[0.08em] text-ivory transition-colors hover:bg-plum/90"
                  >
                    Explore the BloomParentify System — {PRICE}
                    <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </PopupShell>
              </motion.div>
            )}

            {mode === "discount" && !submitted && (
              <motion.div
                key="discount"
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <PopupShell icon={Sparkles} eyebrow="A little something extra" testId="discount-popup">
                  <DialogHeader>
                    <DialogTitle className="mt-3 text-center font-serif text-3xl font-medium leading-tight tracking-tight text-plum">
                      Want 10% off the complete BloomParentify System?
                    </DialogTitle>
                    <DialogDescription className="mt-3 text-center text-base leading-relaxed text-plumcharcoal/70">
                      If you're ready to make everyday life more manageable—and
                      create more room for what's next—you can unlock a special
                      first-purchase offer.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-5 flex items-center justify-center gap-3 rounded-2xl bg-blushcream px-5 py-4">
                    <p className="font-serif text-xl text-plumcharcoal/50 line-through">{PRICE}</p>
                    <ArrowRight size={16} strokeWidth={1.5} className="text-plumcharcoal/40" />
                    <p className="font-serif text-3xl font-medium text-plum" data-testid="discount-price">{DISCOUNT.price}</p>
                    <span className="rounded-lg bg-plum px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold">
                      Save 10%
                    </span>
                  </div>
                  <EmailForm
                    source="discount"
                    buttonLabel="Unlock my 10% off"
                    onSuccess={handleDiscountSuccess}
                    testIdPrefix="discount"
                  />
                  <p className="mt-3 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-plumcharcoal/45">
                    Your personal offer is valid for 15 minutes.
                  </p>
                </PopupShell>
              </motion.div>
            )}

            {mode === "discount" && submitted && (
              <DiscountSuccess key="discount-success" reduce={reduce} onClose={() => setOpenSafe(false)} />
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
      <DiscountBar />
    </>
  );
};

const DiscountSuccess = ({ reduce, onClose }) => {
  const { purchase, dialog } = usePurchase(DISCOUNT_PAYMENT_URL);
  const time = useCountdown(() => {
    track("discount_expired");
    window.dispatchEvent(new CustomEvent("bp-state"));
  });

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      data-testid="discount-success"
    >
      <PopupShell icon={Sparkles} eyebrow="Your personal offer is active" testId="discount-active-view">
        <DialogHeader>
          <DialogTitle className="mt-3 text-center font-serif text-3xl font-medium leading-tight tracking-tight text-plum">
            Your 10% is unlocked.
          </DialogTitle>
        </DialogHeader>
        <div className="mt-5 rounded-2xl bg-blushcream px-5 py-5 text-center">
          <div className="flex items-center justify-center gap-3">
            <p className="font-serif text-xl text-plumcharcoal/50 line-through">{PRICE}</p>
            <p className="font-serif text-4xl font-medium text-plum">{DISCOUNT.price}</p>
            <span className="rounded-lg bg-plum px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold">
              Save 10%
            </span>
          </div>
          <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.2em] text-rose">
            Your 10% offer expires in
          </p>
          <p className="mt-1 font-serif text-3xl tabular-nums text-plum" data-testid="discount-timer">
            {time}
          </p>
        </div>
        <button
          type="button"
          data-testid="discount-checkout-button"
          data-analytics-event="discount_checkout_click"
          onClick={() => purchase("discount_checkout_click")}
          className="group mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-rose px-6 py-4 text-sm font-bold uppercase tracking-[0.08em] text-warmwhite shadow-[0_10px_26px_rgba(201,130,150,0.4)] transition-colors duration-200 hover:bg-[#b96f84]"
        >
          Get the system for {DISCOUNT.price}
          <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full text-center text-xs font-semibold uppercase tracking-[0.14em] text-plumcharcoal/50 transition-colors hover:text-plum"
        >
          Continue browsing
        </button>
      </PopupShell>
      {dialog}
    </motion.div>
  );
};

export const DiscountBar = () => {
  const [active, setActive] = useState(isDiscountActive());
  const { purchase, dialog } = usePurchase(DISCOUNT_PAYMENT_URL);
  const time = useCountdown(() => {
    track("discount_expired");
    setActive(false);
  });

  useEffect(() => {
    const update = () => setActive(isDiscountActive());
    window.addEventListener("bp-state", update);
    return () => window.removeEventListener("bp-state", update);
  }, []);

  if (!DISCOUNT_ENABLED || !active) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-x-3 bottom-3 z-40 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[340px]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="discount-bar"
      >
        <div className="flex items-center gap-3 rounded-2xl border border-gold/40 bg-plum px-4 py-3.5 shadow-[0_18px_50px_rgba(90,48,73,0.45)]">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
              Your 10% offer · <span className="tabular-nums" data-testid="discount-bar-timer">{time}</span>
            </p>
            <p className="truncate font-serif text-lg italic text-ivory">
              {PRICE} → {DISCOUNT.price}
            </p>
          </div>
          <button
            type="button"
            data-testid="discount-bar-button"
            data-analytics-event="discount_checkout_click"
            onClick={() => purchase("discount_checkout_click")}
            className="shrink-0 rounded-xl bg-rose px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-warmwhite transition-colors hover:bg-[#b96f84]"
          >
            Get it
          </button>
        </div>
        {dialog}
      </motion.div>
    </AnimatePresence>
  );
};
